import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Users, Calendar, Clock, Sparkles } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PaymentModal from '../payment/PaymentModal';
import { useTranslation } from '../../context/LanguageContext';

const PricingPage = ({ onLogin }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Get current user from localStorage
    const getUserFromStorage = () => {
        const userUuid = localStorage.getItem('userUuid');
        const userEmail = localStorage.getItem('userEmail');
        const userName = localStorage.getItem('userName');

        if (userUuid) {
            return {
                peopleUuid: userUuid,
                email: userEmail,
                name: userName
            };
        }
        return null;
    };

    const plans = [
        {
            id: 'bronze',
            name: t.plans?.bronze?.name || 'Bronze',
            price: '0',
            period: t.pricing?.perMonth || '/month',
            description: t.plans?.bronze?.description || 'To discover Cabaza',
            features: [
                { text: t.features?.upTo5Contacts || 'Up to 5 contacts', included: true },
                { text: t.features?.businessCardScan || 'Business card scan', included: true },
                { text: t.features?.detailedContactForm || 'Detailed contact form', included: true },
                { text: t.features?.groupCreation || 'Group creation', included: false },
                { text: t.features?.unlimitedContacts || 'Unlimited contacts', included: false },
                { text: t.features?.createEvent || 'Create an event', included: false },
                { text: t.features?.itemsForSale || 'Items for sale', included: false },
                { text: t.features?.offerServices || 'Offer services', included: false },
                { text: t.features?.integratedCalendar || 'Integrated calendar', included: false },
                { text: t.features?.availabilityManagement || 'Availability management', included: false },
            ],
            cta: t.pricing?.startFree || 'Start for free',
            ctaLink: '/register',
            popular: false,
            gradient: false,
            isPaid: false,
        },
        {
            id: 'argent',
            name: t.plans?.silver?.name || 'Silver',
            price: '4',
            period: t.pricing?.perMonth || '/month',
            description: t.plans?.silver?.description || 'For professionals',
            features: [
                { text: t.features?.unlimitedContacts || 'Unlimited contacts', included: true },
                { text: t.features?.businessCardScan || 'Business card scan', included: true },
                { text: t.features?.detailedContactForm || 'Detailed contact form', included: true },
                { text: t.features?.groupCreation || 'Group creation', included: true },
                { text: t.features?.exportContacts || 'Export contacts', included: true },
                { text: t.features?.createEvent || 'Create an event', included: true },
                { text: t.features?.itemsForSale || 'Items for sale', included: true },
                { text: t.features?.offerServices || 'Offer services', included: false },
                { text: t.features?.integratedCalendar || 'Integrated calendar', included: false },
                { text: t.features?.availabilityManagement || 'Availability management', included: false },
            ],
            cta: t.pricing?.chooseSilver || 'Choose Silver',
            ctaLink: '/register',
            popular: true,
            gradient: true,
            isPaid: true,
        },
        {
            id: 'or',
            name: t.plans?.gold?.name || 'Gold',
            price: '8',
            period: t.pricing?.perMonth || '/month',
            description: t.plans?.gold?.description || 'The complete solution',
            features: [
                { text: t.features?.unlimitedContacts || 'Unlimited contacts', included: true },
                { text: t.features?.businessCardScan || 'Business card scan', included: true },
                { text: t.features?.detailedContactForm || 'Detailed contact form', included: true },
                { text: t.features?.groupCreation || 'Group creation', included: true },
                { text: t.features?.exportContacts || 'Export contacts', included: true },
                { text: t.features?.createEvent || 'Create an event', included: true },
                { text: t.features?.itemsForSale || 'Items for sale', included: true },
                { text: t.features?.offerServices || 'Offer services', included: true },
                { text: t.features?.integratedCalendar || 'Integrated calendar', included: true },
                { text: t.features?.availabilityManagement || 'Availability management', included: true },
            ],
            cta: t.pricing?.chooseGold || 'Choose Gold',
            ctaLink: '/register',
            popular: false,
            gradient: false,
            isPaid: true,
        },
    ];

    const handlePlanSelect = (plan) => {
        const user = getUserFromStorage();

        if (!user) {
            // User not logged in, redirect to register with selected plan
            navigate('/register', { state: { selectedPlan: plan.id } });
            return;
        }

        if (plan.isPaid) {
            // User logged in, show payment modal
            setSelectedPlan(plan.id);
            setShowPaymentModal(true);
        } else {
            // Free plan and already logged in - go to dashboard
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Header onLogin={onLogin} />

            <main className="flex-1">
                {/* Hero Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        {t.pricing?.title || 'Choose your'} <span className="text-cabaza-purple">{t.pricing?.titleHighlight || 'plan'}</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        {t.pricing?.subtitle || 'Simple and transparent pricing. No hidden fees.'}
                    </p>
                </div>

                {/* Pricing Cards */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {plans.map((plan) => (
                            <div
                                key={plan.name}
                                className={`relative rounded-2xl p-8 ${
                                    plan.popular
                                        ? 'bg-linear-to-br from-cabaza-purple to-cabaza-pink text-white shadow-2xl scale-105 z-10'
                                        : 'bg-white border-2 border-gray-100 shadow-lg hover:shadow-xl hover:border-cabaza-purple/20'
                                } transition-all duration-300`}
                            >
                                {/* Popular Badge */}
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <span className="inline-flex items-center gap-1 bg-white text-cabaza-purple px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                                            <Sparkles className="w-4 h-4" />
                                            {t.pricing?.popular || 'Popular'}
                                        </span>
                                    </div>
                                )}

                                {/* Plan Name */}
                                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                                    {plan.name}
                                </h3>
                                <p className={`text-sm mb-6 ${plan.popular ? 'text-white/80' : 'text-gray-500'}`}>
                                    {plan.description}
                                </p>

                                {/* Price */}
                                <div className="mb-8">
                                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                                        {plan.price}€
                                    </span>
                                    <span className={`text-lg ${plan.popular ? 'text-white/80' : 'text-gray-500'}`}>
                                        {plan.period}
                                    </span>
                                </div>

                                {/* Features */}
                                <ul className="space-y-4 mb-8">
                                    {plan.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-center gap-3">
                                            {feature.included ? (
                                                <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                                    plan.popular ? 'bg-white/20' : 'bg-green-100'
                                                }`}>
                                                    <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-green-600'}`} />
                                                </div>
                                            ) : (
                                                <div className={`shrink-0 w-5 h-5 rounded-full ${
                                                    plan.popular ? 'bg-white/10' : 'bg-gray-100'
                                                }`} />
                                            )}
                                            <span className={`text-sm ${
                                                feature.included
                                                    ? plan.popular ? 'text-white' : 'text-gray-700'
                                                    : plan.popular ? 'text-white/50' : 'text-gray-400'
                                            }`}>
                                                {feature.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA Button */}
                                {plan.isPaid ? (
                                    <button
                                        onClick={() => handlePlanSelect(plan)}
                                        className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 ${
                                            plan.popular
                                                ? 'bg-white text-cabaza-purple hover:bg-gray-100 hover:shadow-lg'
                                                : 'bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-105'
                                        }`}
                                    >
                                        {plan.cta}
                                    </button>
                                ) : (
                                    <Link
                                        to={plan.ctaLink}
                                        className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 ${
                                            plan.popular
                                                ? 'bg-white text-cabaza-purple hover:bg-gray-100 hover:shadow-lg'
                                                : 'bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-105'
                                        }`}
                                    >
                                        {plan.cta}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Features Section */}
                <div className="bg-gray-50 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                            {t.pricing?.featuresTitle || 'Features included'}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="bg-white rounded-xl p-6 shadow-sm">
                                <div className="w-12 h-12 bg-cabaza-purple/10 rounded-xl flex items-center justify-center mb-4">
                                    <Users className="w-6 h-6 text-cabaza-purple" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.features?.contactManagement || 'Contact management'}</h3>
                                <p className="text-gray-600 text-sm">
                                    {t.features?.contactManagementDesc || 'Organize your contacts with detailed forms and create groups for better management.'}
                                </p>
                            </div>
                            <div className="bg-white rounded-xl p-6 shadow-sm">
                                <div className="w-12 h-12 bg-cabaza-purple/10 rounded-xl flex items-center justify-center mb-4">
                                    <Calendar className="w-6 h-6 text-cabaza-purple" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.features?.integratedCalendar || 'Integrated calendar'}</h3>
                                <p className="text-gray-600 text-sm">
                                    {t.features?.calendarDesc || 'Plan your appointments directly from the app and sync with your calendars.'}
                                </p>
                            </div>
                            <div className="bg-white rounded-xl p-6 shadow-sm">
                                <div className="w-12 h-12 bg-cabaza-purple/10 rounded-xl flex items-center justify-center mb-4">
                                    <Clock className="w-6 h-6 text-cabaza-purple" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.features?.availabilityManagement || 'Availability'}</h3>
                                <p className="text-gray-600 text-sm">
                                    {t.features?.availabilityDesc || 'Share your availability and let your contacts book appointments easily.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Methods Section */}
                <div className="py-12 bg-white">
                    <div className="max-w-3xl mx-auto px-4 text-center">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t.pricing?.paymentMethods || 'Accepted payment methods'}</h3>
                        <div className="flex items-center justify-center gap-6 flex-wrap">
                            <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Credit Card" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-8" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/applepay.svg" alt="Apple Pay" className="h-8" />
                        </div>
                        <p className="text-sm text-gray-500 mt-4">{t.pricing?.securedBy || 'Secured payments via Mollie'}</p>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                        {t.pricing?.faq?.title || 'Frequently Asked Questions'}
                    </h2>
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q1 || 'Can I change my plan at any time?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a1 || 'Yes, you can upgrade or downgrade at any time. The change takes effect immediately.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q2 || 'Is there a commitment?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a2 || 'No, all our plans are commitment-free. You can cancel at any time.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q3 || 'What happens if I exceed 5 contacts on the Bronze plan?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a3 || 'You will be prompted to upgrade to Silver to continue adding contacts. Your existing contacts remain accessible.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq?.q4 || 'How does payment work?'}</h3>
                            <p className="text-gray-600 text-sm">
                                {t.pricing?.faq?.a4 || 'Payment is monthly and secured via Mollie. You can pay with Bancontact, credit card, PayPal, or iDEAL.'}
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />

            {/* Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                planType={selectedPlan}
                user={getUserFromStorage()}
            />
        </div>
    );
};

export default PricingPage;
